/**
 * Chat send and abort operations.
 *
 * Wraps chat.send and chat.abort protocol methods.
 * Handles idempotency key generation and timeout configuration.
 */

import type { WSClient } from '../ws/client';
import type {
  ChatSendParams,
  ChatSendPayload,
  ChatAbortParams,
  ChatAttachment,
  SessionSteerParams,
  SessionSteerPayload,
} from '../types/protocol';

/** Options for sending a chat message */
export interface SendMessageOptions {
  /** File/audio/image attachments */
  attachments?: ChatAttachment[];
  /** Thinking/reasoning mode hint (e.g., "enabled") */
  thinking?: string;
  /** Idempotency key for deduplication. Auto-generated if omitted. */
  idempotencyKey?: string;
  /** Request timeout in milliseconds. Uses client default if omitted. */
  timeoutMs?: number;
}

/**
 * Send a chat message to the gateway.
 *
 * Constructs a `chat.send` request frame and sends it via the WS client.
 * An idempotency key is auto-generated if not provided, ensuring safe
 * retries without duplicate message processing.
 *
 * @param client - The WebSocket client instance
 * @param sessionKey - Session key (e.g., "agent:main:main")
 * @param message - The user message text
 * @param options - Optional send configuration
 * @returns The runId for tracking the streaming response
 *
 * @example
 * ```ts
 * const runId = await sendChatMessage(client, 'agent:main:main', 'Hello!');
 * // runId can be used with abortChat() or to correlate streaming events
 * ```
 */
export async function sendChatMessage(
  client: WSClient,
  sessionKey: string,
  message: string,
  options?: SendMessageOptions,
): Promise<string> {
  const params: ChatSendParams = {
    sessionKey,
    message,
    idempotencyKey: options?.idempotencyKey ?? crypto.randomUUID(),
    ...(options?.attachments && { attachments: options.attachments }),
    ...(options?.thinking && { thinking: options.thinking }),
  };

  const payload = await client.request<ChatSendParams, ChatSendPayload>(
    'chat.send',
    params,
    options?.timeoutMs,
  );

  return payload.runId;
}

/**
 * Interrupt an active reply on the target session and send the new message.
 *
 * When the session is idle, `sessions.steer` behaves like a normal send. When
 * a run is active, the gateway aborts the current run and starts a fresh one.
 */
export async function steerSessionMessage(
  client: WSClient,
  sessionKey: string,
  message: string,
  options?: SendMessageOptions,
): Promise<string> {
  const params: SessionSteerParams = {
    key: sessionKey,
    message,
    idempotencyKey: options?.idempotencyKey ?? crypto.randomUUID(),
    ...(options?.attachments && { attachments: options.attachments }),
    ...(options?.thinking && { thinking: options.thinking }),
  };

  const payload = await client.request<SessionSteerParams, SessionSteerPayload>(
    'sessions.steer',
    params,
    options?.timeoutMs,
  );

  return payload.runId;
}

/**
 * Abort the current or a specific running chat request.
 *
 * Sends a `chat.abort` request to the gateway. If no `runId` is specified,
 * the gateway aborts whichever run is currently active on the session.
 *
 * @param client - The WebSocket client instance
 * @param sessionKey - Session key (e.g., "agent:main:main")
 * @param runId - Specific run to abort. If omitted, aborts the active run.
 *
 * @example
 * ```ts
 * // Abort a specific run
 * await abortChat(client, 'agent:main:main', runId);
 *
 * // Abort whatever is currently running
 * await abortChat(client, 'agent:main:main');
 * ```
 */
export async function abortChat(
  client: WSClient,
  sessionKey: string,
  runId?: string,
): Promise<void> {
  const params: ChatAbortParams = {
    sessionKey,
    ...(runId && { runId }),
  };

  await client.request<ChatAbortParams, unknown>('chat.abort', params);
}
